import { useState } from 'react';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/tooltip';

/**
 * Props for the InfoIcon component
 */
interface InfoIconProps {
  size?: number;
  display?: boolean;
  content?: string | React.ReactNode;
  /** Accessible name of the icon button ("About Total turnover"). */
  label?: string;
  /** Class of the icon button, to color or align it. */
  className?: string;
}

/**
 * A component that displays an info icon with a tooltip.
 * The icon size can be customized with the `size` prop.
 * The tooltip content can be customized with the `content` prop.
 * The tooltip will not be displayed if `display` is set to `false`.
 * Hover or keyboard focus opens it, and so does a click or tap (touch screens
 * have no hover); a click never submits a surrounding form.
 */
export function InfoIcon({
  size = 14,
  display = true,
  content,
  label,
  className,
}: InfoIconProps) {
  const [open, setOpen] = useState(false);

  if (!display) return null;

  if (typeof content === 'string') {
    content = <p className="w-2xs text-center">{content}</p>;
  }

  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger
        type="button"
        aria-label={label}
        className={className}
        onClick={(event) => {
          // Radix closes the tooltip on click; a tap should open it instead.
          event.preventDefault();
          setOpen(true);
        }}
      >
        <Info size={size} aria-hidden="true" />
      </TooltipTrigger>

      <TooltipContent className="bg-white text-gray-800 shadow-md border border-gray-100">
        {content}
      </TooltipContent>
    </Tooltip>
  );
}
